import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useForm, Controller, SubmitHandler, Control, FieldErrors, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { Card, FloatLabel, InputText, SCButton, InputNumber, Calendar } from '../../Common/Imports';
import { ApiResponse, ImportData_EXT } from '../../Models/Classes';
import { useToast } from '../../Helpers/ToastHelper';

interface DataImportProp {
    IdImportData: number;
    onSaved?: () => void;
}

interface FieldProps<T extends FieldValues> {
    label: string;
    id: Path<T>;  // Ensures id is a valid key from the form model
    control: Control<T>;
    errors: FieldErrors<T>;
    disabled: boolean;
}

const DataImport: React.FC<DataImportProp> = ({ IdImportData, onSaved }) => {
    const { t } = useTranslation();
    const [data, setData] = useState<ImportData_EXT | null>(null);
    const [allowEdit, setAllowEdit] = useState<boolean>(false);
    const AxiosHelper = useAxios();
    const { showToast } = useToast();

    const { control, handleSubmit, reset, formState: { errors } } = useForm<ImportData_EXT>({
        defaultValues: {} as ImportData_EXT,
    });

    const saveData = async (formData: ImportData_EXT, requeue: boolean) => {
        try {
            const formattedData = {
                ...formData,
                checkInDate: formData.checkInDate.toISOString(),
                checkOutDate: formData.checkOutDate.toISOString(),
                bookingDate: formData.bookingDate?.toISOString() || null,
                pax1BirthDate: formData.pax1BirthDate?.toISOString() || null,
                pax2BirthDate: formData.pax2BirthDate?.toISOString() || null,
                pax3BirthDate: formData.pax3BirthDate?.toISOString() || null,
                pax4BirthDate: formData.pax4BirthDate?.toISOString() || null,
                // If requeue, set these fields to null
                importInfoType: requeue ? null : formData.importInfoType,
                importInfo: requeue ? null : formData.importInfo,
            };
            try {
                const _Resp = await AxiosHelper.post<ApiResponse<boolean>>(`Imports/SaveSingle`, formattedData);
                if (_Resp.data.data) {
                    showToast("success", "Success", t("Common.Toasts.Save.Success"));
                    await GetData();
                    onSaved?.();
                } else {
                    showToast("error", "Error", t("Common.Toasts.Save.Error"));
                }
            } catch (error) {
                showToast("error", "Error", t("Common.Toasts.Save.Error"));
                console.error('Error fetching data:', error);
            }
        } catch (error) {
            console.error('Error saving data:', error);
        }
    };

    const onSubmit: SubmitHandler<ImportData_EXT> = async (formData) => {
        await saveData(formData, false);
    };

    const onSaveAndRequeue = async () => {
        handleSubmit(async (formData) => {
            await saveData(formData, true);
        })();
    };

    const GetData = useCallback(async () => {
        try {
            const resp = await AxiosHelper.get<ApiResponse<ImportData_EXT>>(`Imports/GetSingle/${IdImportData}`);
            if (resp.data.code === 0) {
                const item = resp.data.data;
                setAllowEdit(!(item.importInfoType === 'W' || item.importInfoType === "E"));
                setData(item);

                // Reset form with data
                reset({
                    ...item,
                    checkInDate: new Date(item.checkInDate),
                    checkOutDate: new Date(item.checkOutDate),
                    bookingDate: item.bookingDate ? new Date(item.bookingDate) : null,
                    pax1BirthDate: item.pax1BirthDate ? new Date(item.pax1BirthDate) : null,
                    pax2BirthDate: item.pax2BirthDate ? new Date(item.pax2BirthDate) : null,
                    pax3BirthDate: item.pax3BirthDate ? new Date(item.pax3BirthDate) : null,
                    pax4BirthDate: item.pax4BirthDate ? new Date(item.pax4BirthDate) : null,
                });
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }, [AxiosHelper, IdImportData, reset]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const CardFooter = () => (
        <div className="row">
            <div className="col d-flex justify-content-end gap-2">
                <SCButton Name="Requeue" Label={t("Common.Buttons.SaveRequeue")} OnClick={onSaveAndRequeue} />
                <SCButton Name="Save" Label={t("Common.Buttons.Save")} OnClick={handleSubmit(onSubmit)} />
            </div>
        </div>
    );

    if (!data) return null;

    return (
        <Card footer={CardFooter}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                {data.importInfo && (
                    <div className="row text-center mb-4">
                        <div className="col">
                            <b className="text-danger">{t("Imports.Errors." + data.importInfo)}</b>
                        </div>
                    </div>
                )}

                <div className="row">
                    <SectionTitle title="General Information" />

                    {([
                        "brandName",
                        "contactEmail",
                        "customerReservationCode",
                        "idStakeholder",
                        "mealplanCode",
                        "mealplanName",
                        "providerCode",
                        "providerReservationCode",
                        "reservationStatus",
                        "roomName",
                        "structureAddress",
                        "structureCity",
                        "structureCountry",
                        "structureCustomerCode",
                        "structureEmail",
                        "structureGIATA",
                        "structureISOCountryCode",
                        "structureName",
                        "structurePhone",
                        "structureProviderCode",
                    ] as (keyof ImportData_EXT)[]).map((field) => (
                        <div className="col-md-3 mb-4">
                            <FormField<ImportData_EXT>
                                key={field}
                                label={t(`Imports.Form.${field}`)}
                                id={field}
                                control={control}
                                errors={errors}
                                disabled={allowEdit}
                            />
                        </div>
                    ))}

                    <SectionTitle title="Dates" />
                    {(["bookingDate", "checkInDate", "checkOutDate", "cancellationDate"] as (keyof ImportData_EXT)[]).map((field) => (
                        <div className="col-md-3 mb-4">
                            <DateField<ImportData_EXT> key={field} label={t(`Imports.Form.${field}`)} id={field} control={control} errors={errors} disabled={allowEdit} />
                        </div>
                    ))}

                    {[1, 2, 3, 4].map((index) => (
                        <>
                            <SectionTitle title={`Passenger ${index}`} />
                            <React.Fragment key={index}>
                                {(["Name", "Surname", "Age", "BirthDate", "Gender"] as const).map((field) => (
                                    field === "Age" ? (
                                        <div className="col-md-2 mb-4">
                                            <NumberField<ImportData_EXT>
                                                key={`pax${index}${field}`}
                                                label={`Pax ${index} ${field}`}
                                                id={`pax${index}${field}` as keyof ImportData_EXT}
                                                control={control}
                                                errors={errors}
                                                disabled={allowEdit}
                                            />
                                        </div>
                                    ) : field === "Gender" ? (
                                        <div className="col-md-2 mb-4">
                                            <FormField<ImportData_EXT>
                                                key={`pax${index}${field}`}
                                                label={`Pax ${index} ${field}`}
                                                id={`pax${index}${field}` as keyof ImportData_EXT}
                                                control={control}
                                                errors={errors}
                                                disabled={allowEdit}
                                            />
                                        </div>
                                    )
                                        : field === "BirthDate" ? (
                                            <div className="col-md-2 mb-4">
                                                <DateField<ImportData_EXT>
                                                    key={`pax${index}${field}`}
                                                    label={`Pax ${index} ${field}`}
                                                    id={`pax${index}${field}` as keyof ImportData_EXT}
                                                    control={control}
                                                    errors={errors}
                                                    disabled={allowEdit}
                                                />
                                            </div>
                                        ) : (
                                            <div className="col-md-3 mb-4">
                                                <FormField<ImportData_EXT>
                                                    key={`pax${index}${field}`}
                                                    label={`Pax ${index} ${field}`}
                                                    id={`pax${index}${field}` as keyof ImportData_EXT}
                                                    control={control}
                                                    errors={errors}
                                                    disabled={allowEdit}
                                                />
                                            </div>
                                        )
                                ))}
                            </React.Fragment>
                        </>
                    ))}
                </div>
            </form>
        </Card>
    );
};

export default DataImport;

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
    <div className="col-12 mb-3">
        <h5>{title}</h5>
    </div>
);

export const FormField = <T extends FieldValues>({ label, id, control, errors, disabled }: FieldProps<T>) => {
    // Fix: Ensure error message is always a string
    const errorMessage = errors[id]?.message ? String(errors[id]?.message) : undefined;

    return (
        <>
            <FloatLabel>
                <label htmlFor={id}>{label}</label>
                <Controller
                    name={id}
                    control={control}
                    render={({ field }) => (
                        <InputText
                            disabled={disabled}
                            id={id}
                            {...field}
                            className={classNames({ 'p-invalid': !!errors[id] })}
                        />
                    )}
                />
            </FloatLabel>
            {errorMessage && <small className="p-error">{errorMessage}</small>}
        </>
    );
};

export const DateField = <T extends FieldValues>({ label, id, control, errors, disabled }: FieldProps<T>) => {
    const errorMessage = errors[id]?.message ? String(errors[id]?.message) : undefined;

    return (
        <>
            <FloatLabel>
                <label htmlFor={id}>{label}</label>
                <Controller
                    name={id}
                    control={control}
                    render={({ field }) => (
                        <Calendar
                            disabled={disabled}
                            id={id}
                            value={field.value}
                            onChange={(e) => field.onChange(e.value)}
                            className={classNames({ 'p-invalid': !!errors[id] })}
                        />
                    )}
                />
            </FloatLabel>
            {errorMessage && <small className="p-error">{errorMessage}</small>}
        </>
    );
};

export const NumberField = <T extends FieldValues>({ label, id, control, errors, disabled }: FieldProps<T>) => {
    const errorMessage = errors[id]?.message ? String(errors[id]?.message) : undefined;

    return (
        <>
            <FloatLabel>
                <label htmlFor={id}>{label}</label>
                <Controller
                    name={id}
                    control={control}
                    render={({ field }) => (
                        <InputNumber
                            disabled={disabled}
                            id={id}
                            value={field.value}
                            onValueChange={(e) => field.onChange(e.value)}
                            className={classNames({ 'p-invalid': !!errors[id] })}
                        />
                    )}
                />
            </FloatLabel>
            {errorMessage && <small className="p-error">{errorMessage}</small>}
        </>
    );
};